import { PendenciaKanban, StatusKanbanType, ResultadoType } from './types';
import {
    TIPOS_PENDENCIA,
    TIPOS_PENDENCIA_LABELS,
    TIPOS_PENDENCIA_DESCRICOES,
    TIPOS_PENDENCIA_LABELS_DESCRICAO,
    formatTipo,
    getTipoIcon,
    isTipoFinanceiro,
    isTipoDocumentacao,
    isTipoInformativa,
} from '../../../utility/pendenciaTipos';

export { TIPOS_PENDENCIA, TIPOS_PENDENCIA_LABELS, TIPOS_PENDENCIA_DESCRICOES, TIPOS_PENDENCIA_LABELS_DESCRICAO, formatTipo, getTipoIcon, isTipoFinanceiro, isTipoDocumentacao, isTipoInformativa };

// bg via CSS var (reativo ao tema): pastel no light, tinta escura no dark,
// mantendo o texto/ícone do header legível nos dois temas (ver index.css --kanban-*).
export const CORES_COLUNAS = {
    PENDENTES: {
        bg: 'var(--kanban-pendentes)',
        icon: 'rgb(67, 97, 205)',
    },
    EM_ANDAMENTO: {
        bg: 'var(--kanban-andamento)',
        icon: 'rgb(241, 143, 0)',
    },
    CONCLUIDAS: {
        bg: 'var(--kanban-concluidas)',
        icon: 'rgb(15, 199, 88)',
    },
} as const;

export { formatCurrency } from '../../../utility/currencyUtils';

export interface ArquivoPendenciaBackend {
    id_proposta_arquivo: number;
    caminho: string;
    tipo_arquivo?: string;
}

export interface PendenciaBackend {
    id_pendencia: number;
    id_proposta: number;
    tipo: string;
    descricao: string;
    valor?: number;
    destino: string;
    resolvida: string;
    status_kanban: string;
    resultado: string | null;
    resposta?: string | null;
    criado: string;
    modificado: string;
    origem: string;
    criadorNome?: string;
    destinatarioNome?: string | null;
    solicitanteNome?: string | null;
    id_responsavel?: number | null;
    id_destino?: number | null;
    tipo_responsavel?: string | null;
    arquivos?: ArquivoPendenciaBackend[];
}

export const formatarTempoRelativoPendencia = (data?: string | number | Date | null): string => {
    if (!data) {
        return "";
    }

    const dataReferencia = new Date();
    const dataInformada = data instanceof Date ? data : new Date(data);

    if (Number.isNaN(dataInformada.getTime())) {
        return "";
    }

    const MILISSEGUNDO = 1000;
    const SEGUNDO = 60;
    const MINUTO = 60;
    const HORA_EM_MILISSEGUNDOS = MILISSEGUNDO * SEGUNDO * MINUTO;
    const DIA_EM_MILISSEGUNDOS = HORA_EM_MILISSEGUNDOS * 24;

    const diferencaEmMilissegundos = dataReferencia.getTime() - dataInformada.getTime();

    if (diferencaEmMilissegundos < HORA_EM_MILISSEGUNDOS) {
        const minutos = Math.floor(diferencaEmMilissegundos / (MILISSEGUNDO * SEGUNDO));
        if (minutos < 1) {
            return "Há menos de 1 minuto";
        }
        const textoMinutos = minutos === 1 ? "1 minuto" : `${minutos} minutos`;
        return `Há ${textoMinutos}`;
    }

    if (diferencaEmMilissegundos < DIA_EM_MILISSEGUNDOS) {
        const horas = Math.floor(diferencaEmMilissegundos / HORA_EM_MILISSEGUNDOS);
        const textoHoras = horas === 1 ? "1 hora" : `${horas} horas`;
        return `Há ${textoHoras}`;
    }

    const dias = Math.floor(diferencaEmMilissegundos / DIA_EM_MILISSEGUNDOS);
    const textoDias = dias === 1 ? "1 dia" : `${dias} dias`;
    return `Há ${textoDias}`;
};

export const mapPendenciaFromBackend = (pendencia: PendenciaBackend): PendenciaKanban => {
    const mapStatusKanban = (status: string): StatusKanbanType => {
        const statusLower = status.toLowerCase();
        switch (statusLower) {
            case 'pendente':
                return 'Pendentes';
            case 'em_andamento':
                return 'Em Andamento';
            case 'concluida':
                return 'Concluídas';
            default:
                return 'Pendentes';
        }
    };

    const mapResultado = (resultado: string | null): ResultadoType => {
        if (!resultado) return null;
        const resultadoLower = resultado.toLowerCase();
        switch (resultadoLower) {
            case 'resolvida':
                return 'Resolvida';
            case 'aceita':
                return 'Aceita';
            case 'recusada':
                return 'Recusada';
            default:
                return null;
        }
    };

    return {
        id_pendencia: pendencia.id_pendencia,
        id_proposta: pendencia.id_proposta,
        tipo: pendencia.tipo,
        descricao: pendencia.descricao,
        valor: pendencia.valor,
        destino: pendencia.destino,
        resolvida: pendencia.resolvida,
        status_kanban: mapStatusKanban(pendencia.status_kanban),
        resultado: mapResultado(pendencia.resultado),
        resposta: pendencia.resposta || null,
        criado: pendencia.criado,
        modificado: pendencia.modificado,
        origem: pendencia.origem as 'Correspondente' | 'Solicitante' | 'Operador' | undefined,
        criadorNome: pendencia.criadorNome || 'Sistema',
        destinatarioNome: pendencia.destinatarioNome || null,
        solicitanteNome: pendencia.solicitanteNome || null,
        id_responsavel: pendencia.id_responsavel || null,
        id_destino: pendencia.id_destino || null,
        tipo_responsavel: pendencia.tipo_responsavel || null,
        arquivos: pendencia.arquivos || [],
    };
};

/**
 * Verifica se o usuário pode mover um card de pendência
 * 
 * Regras:
 * 1. Operador sempre pode mover qualquer pendência (sem restrições)
 * 2. Profissional/Correspondente só pode mover se for o destinatário (id_destino)
 * 3. Solicitante só pode mover se for o destinatário (id_destino)
 *    - Para solicitante, quando destino é 'solicitante', verifica id_escritorio_solicitante
 * 
 * @param pendencia - A pendência que se deseja mover
 * @param userProfile - O perfil do usuário (comum, Solicitante, operador, admin)
 * @param userId - O ID do usuário logado
 * @param idEscritorioSolicitante - O ID do escritório do solicitante (quando aplicável)
 * @returns true se o usuário pode mover a pendência, false caso contrário
 */
export const podeMoverPendencia = (
    pendencia: PendenciaKanban,
    userProfile?: string,
    userId?: number | null,
    idEscritorioSolicitante?: number | null
): boolean => {
    // Operador e Admin sempre podem mover
    if (userProfile === 'operador' || userProfile === 'admin') {
        return true;
    }

    // Profissional/Correspondente (profile = 'comum')
    if (userProfile === 'comum') {
        if (!userId) {
            return false;
        }
        return pendencia.id_destino === userId;
    }

    // Solicitante
    if (userProfile === 'Solicitante' || userProfile === 'solicitante') {
        const destinoNormalizado = String(pendencia.destino || '').toLowerCase().trim();
        
        if (destinoNormalizado === 'solicitante') {
            return idEscritorioSolicitante !== null && 
                   idEscritorioSolicitante !== undefined && 
                   pendencia.id_destino === idEscritorioSolicitante;
        }
        
        if (!userId) {
            return false;
        }
        return pendencia.id_destino === userId;
    }

    // Por padrão, não permite mover
    return false;
};

